import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';
import { cn } from '@/utils';

/**
 * QuizSubjectiveAnswer - 주관식 답안 컴포넌트
 * EditableSubjectiveAnswer 구조와 일치하는 읽기 전용 버전
 * @module QuizSubjectiveAnswer
 */

/**
 * AnswerMarker - 화살표 아이콘이 있는 파란색 원
 * EditableSubjectiveAnswer의 AnswerMarker와 일치
 */
const AnswerMarker = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex size-[30px] flex-shrink-0 items-center justify-center rounded-full border-[3px] border-white shadow-xs self-start', 'bg-blue-400', className)} {...props} data-sentry-component="AnswerMarker" data-sentry-source-file="QuizSubjectiveAnswer.tsx">
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" className="translate-x-[1px]" viewBox="0 0 9 13" fill="none" data-sentry-element="svg" data-sentry-source-file="QuizSubjectiveAnswer.tsx">
        <path d="M1.25 1.25006L6.97344 6.0196C7.11737 6.13953 7.11737 6.36059 6.97344 6.48053L1.25 11.2501" stroke="white" strokeWidth="2.5" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="QuizSubjectiveAnswer.tsx" />
      </svg>
    </div>;
};
AnswerMarker.displayName = 'QuizSubjectiveAnswerMarker';

/**
 * 주관식 답안 목록을 위한 컨테이너
 * EditableSubjective 컨테이너 구조와 일치
 */
const Container = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('relative flex flex-col gap-2xs', className)} {...rest}>
        {children}
      </div>;
});
Container.displayName = 'QuizSubjectiveAnswerContainer';

/**
 * ItemRoot - 답안 항목의 루트 컨테이너
 * Compound component pattern을 위한 컨테이너
 */
const ItemRoot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('flex w-full items-center gap-sm', className)} {...rest}>
        {children}
      </div>;
});
ItemRoot.displayName = 'QuizSubjectiveAnswerItemRoot';

/**
 * ItemContent - 답안 텍스트 콘텐츠 래퍼
 */
const ItemContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('w-full', className)} {...rest}>
        <span className="w-full break-words text-heading-sm-bold">
          {children}
        </span>
      </div>;
});
ItemContent.displayName = 'QuizSubjectiveAnswerItemContent';

/**
 * Item - Compound component
 * QuizMultipleChoice와 동일한 패턴으로 Marker와 Content를 nested 구조로 제공
 */
const ItemCompound = Object.assign(ItemRoot, {
  Marker: AnswerMarker,
  Content: ItemContent
});

/**
 * Item - Props 기반 래퍼
 * QuizItemCard 등에서 사용하는 기본 API
 */
const Item = memo(forwardRef<HTMLDivElement, {
  text: string;
  markerClassName?: string;
} & ComponentPropsWithoutRef<'div'>>((props, ref) => {
  const {
    text,
    markerClassName,
    ...rest
  } = props;
  return <ItemRoot ref={ref} {...rest}>
        <AnswerMarker className={markerClassName} />
        <ItemContent>
          <span className="text-heading-sm-bold">{text}</span>
        </ItemContent>
      </ItemRoot>;
}));
Item.displayName = 'QuizSubjectiveAnswerItem';
export const QuizSubjectiveAnswer = {
  Container,
  Item,
  ItemCompound
};